import React, { useState, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { createLessonPlan, createHomework } from "../../../../../store/actions/course/index";
import styles from "./styles.module.css";
import UploadMaterial from "./uploadMaterial";

const CreateLesson = ({ showModal, setModal, modalType, setApiCalled }) => {
  const dispatch = useDispatch();
  const materialOptionsRef = useRef();
  const [materialType, setMaterialType] = useState("Link");
  const [showMaterialModal, setShowMaterialModal] = useState(false);

  const [formValues, setFormValues] = useState({
    title: "",
    description: "",
    level: "",
    courseImage: null,
    materials: [],
  });

  const handleClickOutside = (e) => {
    if (materialOptionsRef.current && !materialOptionsRef.current.contains(e.target)) {
      materialOptionsRef.current.style.display = "none";
    }
  };

  useEffect(() => {
    window.addEventListener("mousedown", handleClickOutside);
    return () => window.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      setFormValues((prev) => ({ ...prev, courseImage: e.target.files[0] }));
    }
  };

  const handleAddMaterial = (material) => {
    setFormValues((prev) => ({
      ...prev,
      materials: [...prev.materials, material],
    }));
  };

  const removeMaterial = (index) => {
    setFormValues((prev) => ({
      ...prev,
      materials: prev.materials.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async () => {
    if (!formValues.title) return toast.warn("Please add a title");
    if (!formValues.description) return toast.warn("Please add a description");
    if (modalType === "Lesson" && !formValues.level) return toast.warn("Please specify lesson level");
    if (!formValues.courseImage) return toast.warn("Please upload a cover image");

    const form = new FormData();
    form.append("title", formValues.title);
    form.append("description", formValues.description);
    if (modalType === "Lesson") {
      form.append("level", formValues.level);
    }

    // Use courseImage as the key to match common backend expectations in this project
    if (formValues.courseImage && formValues.courseImage.name) {
      form.append("courseImage", formValues.courseImage, formValues.courseImage.name);
    } else {
      form.append("courseImage", formValues.courseImage);
    }

    // Add materials
    formValues.materials.forEach((m, index) => {
      form.append(`materialType_${index}`, m.type);
      if (m.type === "Link") {
        form.append(`materialValue_${index}`, m.value);
      } else {
        form.append(`materialValue_${index}`, m.value, m.value.name);
      }
    });
    form.append("materialCount", formValues.materials.length);

    const profile = JSON.parse(localStorage.getItem("profile"));
    if (profile?._id) {
      form.append("teacherId", profile._id);
    }



    try {
      document.getElementById("loader").style.display = "flex";
      let result;
      if (modalType === "Lesson") {
        result = await dispatch(createLessonPlan(form));
      } else {
        result = await dispatch(createHomework(form));
      }
      document.getElementById("loader").style.display = "none";

      const successMsg = result?.msg || result?.message || (typeof result === "string" ? result : "");
      if (result?.status === "success" || result?.status === 1 || successMsg.includes("Successfully")) {
        toast.success(`${modalType} Created Successfully`);
        setModal(false);
        setApiCalled((prev) => !prev);
        setFormValues({
          title: "",
          description: "",
          level: "",
          courseImage: null,
          materials: [],
        });
      } else {
        toast.error(result?.message || result?.msg || "Failed to create");
      }
    } catch (e) {
      console.error("Submission failed:", e);
      document.getElementById("loader").style.display = "none";
      toast.error("An error occurred");
    }
  };

  if (!showModal) return null;

  return (
    <>
      <div className={styles.modalBackdrop}>
        <div className={styles.modal}>
          <i
            className={`${styles.closeBtn} fas fa-close`}
            onClick={() => setModal(false)}
          ></i>
          <h3 className={styles.heading}>Create {modalType}</h3>

          <input
            type="text"
            name="title"
            placeholder={`${modalType} Title`}
            className={styles.input}
            value={formValues.title}
            onChange={handleChange}
          />

          <textarea
            name="description"
            placeholder={`${modalType} Description`}
            className={styles.textarea}
            value={formValues.description}
            onChange={handleChange}
          ></textarea>

          {modalType === "Lesson" && (
            <input
              type="text"
              name="level"
              placeholder="Lesson Level (e.g. Beginner, Intermediate)"
              className={styles.input}
              value={formValues.level}
              onChange={handleChange}
            />
          )}

          <div className={styles.chooseFiles}>
            <label className={styles.fileUploadBtn}>
              {formValues.courseImage ? formValues.courseImage.name : "Cover Image"} <i className="fas fa-upload"></i>
              <input type="file" style={{ display: "none" }} onChange={handleFileChange} />
            </label>

            <div className={styles.addMaterialWrapper}>
              <button
                type="button"
                className={styles.addMaterialBtn}
                onClick={() => {
                  if (materialOptionsRef.current) materialOptionsRef.current.style.display = "block";
                }}
              >
                Add Material <i className="fas fa-plus"></i>
              </button>
              <ul className={styles.materialOptions} ref={materialOptionsRef}>
                <li
                  onClick={() => {
                    setMaterialType("Link");
                    setShowMaterialModal(true);
                    materialOptionsRef.current.style.display = "none";
                  }}
                >
                  Link
                </li>
                <li
                  onClick={() => {
                    setMaterialType("File");
                    setShowMaterialModal(true);
                    materialOptionsRef.current.style.display = "none";
                  }}
                >
                  File
                </li>
              </ul>
            </div>
          </div>

          {formValues.materials.length > 0 && (
            <div className={styles.materialsList}>
              <h4>Materials:</h4>
              <ul>
                {formValues.materials.map((m, i) => (
                  <li key={i}>
                    {m.type === "Link" ? (
                      <span title={m.value}><i className="fas fa-link"></i> {m.value.length > 30 ? m.value.substring(0, 30) + "..." : m.value}</span>
                    ) : (
                      <span><i className="fas fa-file"></i> {m.value.name}</span>
                    )}
                    <i className="fas fa-trash" onClick={() => removeMaterial(i)} style={{ color: 'red', cursor: 'pointer', marginLeft: '10px' }}></i>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <button className={styles.submitButton} onClick={handleSubmit}>
            Submit <i className="fas fa-check-circle"></i>
          </button>
        </div>
      </div>

      <UploadMaterial
        materialType={materialType}
        showMaterialModal={showMaterialModal}
        setShowMaterialModal={setShowMaterialModal}
        onAdd={handleAddMaterial}
      />
    </>
  );
};

export default CreateLesson;
